/**
 * Legal documents rendered by {@link LegalModal} — the "Terms of Use" and
 * "Privacy Policy" reachable from the login footer. The copy is product-facing
 * legal text and, per the frontend conventions, stays in PT-BR (LGPD is a
 * Brazilian statute); only identifiers and comments are in English.
 *
 * Shared by every portal. The greeting and the scope line are parameterized
 * with the portal name so the same source reads correctly across portals.
 */

/** Which legal document to show. */
export type LegalDocKind = 'terms' | 'privacy';

/** A single renderable block inside a legal document. */
export type LegalBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

/** A full legal document: title, bold lead line, then a flow of blocks. */
export interface LegalDoc {
  title: string;
  lead: string;
  blocks: LegalBlock[];
}

/**
 * Builds both legal documents for a given portal. `portalName` is the display
 * name (e.g. "Portal do Administrador") woven into the welcome/scope lines.
 */
export function getLegalDocs(portalName: string): Record<LegalDocKind, LegalDoc> {
  return {
    terms: {
      title: 'Termos de Uso',
      lead: `Bem-vindo ao ${portalName} da TMR`,
      blocks: [
        {
          kind: 'paragraph',
          text: 'Ao acessar ou utilizar este Portal, você declara estar autorizado a representar sua organização e concorda com estes Termos de Uso.',
        },
        {
          kind: 'paragraph',
          text: 'O Portal destina-se exclusivamente à administração de contas, usuários, permissões, configurações e demais funcionalidades disponibilizadas pela TMR.',
        },
        { kind: 'heading', text: '1. Aceitação' },
        {
          kind: 'paragraph',
          text: 'O uso do Portal implica a aceitação integral destes Termos e da Política de Privacidade.',
        },
        {
          kind: 'paragraph',
          text: 'Caso não concorde com qualquer disposição, interrompa imediatamente a utilização do serviço.',
        },
        { kind: 'heading', text: '2. Acesso à plataforma' },
        { kind: 'paragraph', text: 'Você é responsável por:' },
        {
          kind: 'list',
          items: [
            'manter suas credenciais em sigilo;',
            'utilizar senhas fortes e autenticação adicional quando disponível;',
            'comunicar imediatamente qualquer suspeita de acesso não autorizado.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Todas as ações realizadas através de sua conta serão consideradas de sua responsabilidade, salvo comprovação de uso indevido decorrente de falha da TMR.',
        },
        { kind: 'heading', text: '3. Uso permitido' },
        {
          kind: 'paragraph',
          text: 'Você concorda em utilizar o Portal apenas para fins legítimos e autorizados.',
        },
        { kind: 'paragraph', text: 'É proibido:' },
        {
          kind: 'list',
          items: [
            'tentar obter acesso não autorizado a sistemas ou dados;',
            'interferir na disponibilidade ou segurança da plataforma;',
            'utilizar o Portal para atividades ilícitas;',
            'compartilhar credenciais de acesso;',
            'realizar engenharia reversa, cópia ou exploração não autorizada da plataforma.',
          ],
        },
        { kind: 'heading', text: '4. Administração de usuários' },
        {
          kind: 'paragraph',
          text: 'Administradores podem criar, alterar ou remover usuários, perfis e permissões conforme as funcionalidades disponibilizadas.',
        },
        {
          kind: 'paragraph',
          text: 'É responsabilidade da organização manter essas permissões atualizadas e compatíveis com sua política interna de segurança.',
        },
        { kind: 'heading', text: '5. Disponibilidade' },
        {
          kind: 'paragraph',
          text: 'A TMR busca manter alta disponibilidade da plataforma, porém poderá realizar manutenções, atualizações ou interrupções programadas sempre que necessário.',
        },
        {
          kind: 'paragraph',
          text: 'Também poderão ocorrer indisponibilidades decorrentes de fatores externos, casos fortuitos ou força maior.',
        },
        { kind: 'heading', text: '6. Segurança' },
        {
          kind: 'paragraph',
          text: 'A TMR adota medidas técnicas e organizacionais compatíveis com as melhores práticas de mercado para proteger a plataforma e as informações armazenadas.',
        },
        {
          kind: 'paragraph',
          text: 'Nenhum sistema, entretanto, é completamente imune a riscos de segurança.',
        },
        { kind: 'heading', text: '7. Propriedade intelectual' },
        {
          kind: 'paragraph',
          text: 'Todo o conteúdo, software, marcas, identidade visual, interfaces e tecnologias utilizadas no Portal pertencem à TMR ou aos seus licenciadores e são protegidos pela legislação aplicável.',
        },
        {
          kind: 'paragraph',
          text: 'Nenhum direito de propriedade intelectual é transferido ao usuário.',
        },
        { kind: 'heading', text: '8. Limitação de responsabilidade' },
        { kind: 'paragraph', text: 'A TMR não será responsável por:' },
        {
          kind: 'list',
          items: [
            'uso inadequado da plataforma;',
            'configurações realizadas pelos administradores;',
            'indisponibilidades causadas por terceiros;',
            'falhas decorrentes de equipamentos, redes ou serviços externos.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Na máxima extensão permitida pela legislação, a responsabilidade da TMR limita-se aos danos diretos comprovadamente decorrentes de falha atribuível à plataforma.',
        },
        { kind: 'heading', text: '9. Alterações' },
        {
          kind: 'paragraph',
          text: 'Estes Termos poderão ser atualizados periodicamente para refletir melhorias, mudanças legais ou evolução dos serviços.',
        },
        {
          kind: 'paragraph',
          text: 'A versão mais recente estará sempre disponível no Portal.',
        },
        { kind: 'heading', text: '10. Legislação aplicável' },
        {
          kind: 'paragraph',
          text: 'Estes Termos são regidos pelas leis da República Federativa do Brasil.',
        },
        {
          kind: 'paragraph',
          text: 'Quaisquer controvérsias serão solucionadas conforme a legislação brasileira aplicável.',
        },
        { kind: 'heading', text: '11. Contato' },
        {
          kind: 'paragraph',
          text: 'Em caso de dúvidas sobre estes Termos, entre em contato com a equipe responsável através dos canais oficiais disponibilizados pela TMR.',
        },
      ],
    },
    privacy: {
      title: 'Política de Privacidade',
      lead: 'Compromisso com sua privacidade',
      blocks: [
        {
          kind: 'paragraph',
          text: 'A TMR valoriza a privacidade e a proteção dos dados pessoais tratados em suas plataformas.',
        },
        {
          kind: 'paragraph',
          text: `Esta Política explica como coletamos, utilizamos, armazenamos e protegemos as informações relacionadas ao ${portalName}, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 - LGPD).`,
        },
        { kind: 'heading', text: '1. Dados coletados' },
        {
          kind: 'paragraph',
          text: 'Durante a utilização do Portal, poderão ser tratados dados como:',
        },
        {
          kind: 'list',
          items: [
            'nome;',
            'endereço de e-mail;',
            'cargo ou função;',
            'empresa vinculada;',
            'registros de autenticação;',
            'endereço IP;',
            'informações do navegador e dispositivo;',
            'registros de acesso;',
            'logs de auditoria;',
            'ações realizadas na plataforma.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Os dados coletados variam conforme as funcionalidades utilizadas.',
        },
        { kind: 'heading', text: '2. Finalidade do tratamento' },
        { kind: 'paragraph', text: 'Os dados poderão ser utilizados para:' },
        {
          kind: 'list',
          items: [
            'autenticação de usuários;',
            'administração de contas e permissões;',
            'execução dos serviços contratados;',
            'auditoria e rastreabilidade;',
            'prevenção a fraudes;',
            'atendimento de obrigações legais;',
            'melhoria da experiência da plataforma;',
            'suporte técnico;',
            'monitoramento de segurança.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'A TMR trata apenas os dados necessários para cada finalidade.',
        },
        { kind: 'heading', text: '3. Compartilhamento' },
        {
          kind: 'paragraph',
          text: 'A TMR poderá compartilhar informações apenas quando necessário para:',
        },
        {
          kind: 'list',
          items: [
            'cumprimento de obrigação legal;',
            'execução do contrato;',
            'prestação de serviços por fornecedores autorizados;',
            'determinação judicial ou autoridade competente.',
          ],
        },
        { kind: 'paragraph', text: 'A TMR não comercializa dados pessoais.' },
        { kind: 'heading', text: '4. Armazenamento' },
        {
          kind: 'paragraph',
          text: 'Os dados são armazenados utilizando medidas de segurança compatíveis com padrões modernos de mercado.',
        },
        {
          kind: 'paragraph',
          text: 'O período de retenção observará obrigações legais, requisitos regulatórios e necessidades operacionais.',
        },
        { kind: 'heading', text: '5. Segurança' },
        {
          kind: 'paragraph',
          text: 'A TMR adota medidas destinadas a proteger os dados contra:',
        },
        {
          kind: 'list',
          items: [
            'acesso não autorizado;',
            'alteração indevida;',
            'divulgação;',
            'perda;',
            'destruição acidental.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Entre essas medidas podem estar criptografia, controle de acesso, registros de auditoria, autenticação e monitoramento contínuo.',
        },
        { kind: 'heading', text: '6. Cookies e tecnologias semelhantes' },
        {
          kind: 'paragraph',
          text: 'O Portal poderá utilizar cookies e tecnologias equivalentes para:',
        },
        {
          kind: 'list',
          items: [
            'manter sessões autenticadas;',
            'melhorar desempenho;',
            'armazenar preferências;',
            'garantir segurança;',
            'produzir estatísticas de utilização.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Algumas funcionalidades podem depender desses recursos.',
        },
        { kind: 'heading', text: '7. Direitos do titular' },
        {
          kind: 'paragraph',
          text: 'Nos termos da LGPD, o titular poderá solicitar, quando aplicável:',
        },
        {
          kind: 'list',
          items: [
            'confirmação do tratamento;',
            'acesso aos dados;',
            'correção de informações;',
            'anonimização ou eliminação;',
            'portabilidade;',
            'informações sobre compartilhamentos;',
            'revogação do consentimento, quando esta for a base legal utilizada.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'As solicitações serão analisadas conforme a legislação vigente.',
        },
        { kind: 'heading', text: '8. Logs e auditoria' },
        {
          kind: 'paragraph',
          text: 'Para garantir segurança, conformidade e rastreabilidade, o Portal registra eventos relacionados ao uso da plataforma.',
        },
        {
          kind: 'paragraph',
          text: 'Esses registros podem incluir autenticações, alterações administrativas, configurações e demais ações relevantes realizadas pelos usuários.',
        },
        { kind: 'heading', text: '9. Atualizações desta Política' },
        {
          kind: 'paragraph',
          text: 'Esta Política poderá ser alterada para refletir mudanças legais, regulatórias ou evoluções da plataforma.',
        },
        {
          kind: 'paragraph',
          text: 'A versão vigente estará sempre disponível neste Portal.',
        },
        { kind: 'heading', text: '10. Contato' },
        {
          kind: 'paragraph',
          text: 'Caso tenha dúvidas sobre esta Política de Privacidade ou sobre o tratamento de dados pessoais realizado pela TMR, utilize os canais oficiais de atendimento disponibilizados pela empresa.',
        },
      ],
    },
  };
}
